import * as React from 'react';
import { AlertTriangle, Inbox, SearchX, WifiOff } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Illustration } from '@/components/ui/illustration';
import { Aide } from '@/components/ui/aide';

/**
 * Empty, filtered-empty, error and offline states.
 *
 * These are four different situations and the previous screens either showed
 * nothing at all or the same grey sentence for all of them. Which one is on
 * screen determines what the user should do next, so each has its own copy
 * and its own action:
 *
 *  - `empty`    nothing exists yet        -> create the first one
 *  - `filtered` records exist but hidden  -> clear the filters
 *  - `error`    the request failed        -> retry
 *  - `offline`  no connection             -> explain the queue, retry later
 */
const VARIANTS = {
  empty: {
    icon: Inbox,
    tone: 'text-muted-foreground',
    ring: 'bg-muted',
  },
  filtered: {
    icon: SearchX,
    tone: 'text-muted-foreground',
    ring: 'bg-muted',
  },
  error: {
    icon: AlertTriangle,
    tone: 'text-destructive',
    ring: 'bg-destructive/10',
  },
  offline: {
    icon: WifiOff,
    tone: 'text-flame-ink',
    ring: 'bg-flame/10',
  },
};

/**
 * Au-delà de cette longueur, la description d'un état vide n'est plus un
 * constat mais une explication (« comment ça marche »): elle passe derrière
 * une icône d'aide à côté du titre. Un message d'erreur reste toujours visible.
 */
const SEUIL_MOTS_DESCRIPTION = 12;
const nombreDeMots = (texte) =>
  typeof texte === 'string' ? texte.trim().split(/\s+/).filter(Boolean).length : 0;

const EmptyState = ({
  variant = 'empty',
  icon,
  /**
   * Nom d'une illustration de la marque (`ETATS_VIDES`). Quand elle est
   * fournie, elle remplace la pastille d'icône: une page entièrement vide a
   * la place de respirer, et un dessin y explique mieux qu'un glyphe de 24 px
   * ce que la page contiendra. Les listes filtrées à vide, elles, gardent
   * l'icône — elles s'affichent au milieu d'un écran déjà occupé.
   */
  illustration,
  title,
  description,
  /** Explication complémentaire, toujours rangée derrière l'icône d'aide. */
  aide,
  children,
  action,
  actionLabel,
  onAction,
  secondaryAction,
  className,
  ref,
  ...props
}) => {
  const preset = VARIANTS[variant] ?? VARIANTS.empty;
  const Icon = icon ?? preset.icon;
  const descriptionDansAide =
    variant !== 'error' && nombreDeMots(description) > SEUIL_MOTS_DESCRIPTION;

  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col items-center justify-center rounded-xl border border-dashed border-border px-6 py-12 text-center',
        className,
      )}
      {...props}
    >
      {illustration ? (
        /* Taille native, et non le double autorisé par `Illustration`.
           Un état vide n'occupe pas toujours une page entière: dans les deux
           cartes du bas de l'écran Maintenance, une illustration de 320 px
           écrasait le titre et la phrase qu'elle est censée accompagner. À
           161 px elle reste nette — `srcset` sert la variante ×2 sur les
           écrans qui la demandent — et elle laisse la parole au texte. */
        <Illustration
          name={illustration}
          variant="empty"
          largeurMax={161}
          className="mb-5"
        />
      ) : (
        <div
          className={cn(
            'mb-4 flex h-12 w-12 items-center justify-center rounded-full',
            preset.ring,
          )}
        >
          <Icon className={cn('h-6 w-6', preset.tone)} aria-hidden="true" />
        </div>
      )}

      <div className="flex items-center justify-center gap-1">
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        {(descriptionDansAide || aide) && (
          <Aide titre={typeof title === 'string' ? title : 'cette section'}>
            {descriptionDansAide && <p>{description}</p>}
            {aide}
          </Aide>
        )}
      </div>

      {description && !descriptionDansAide && (
        <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">
          {description}
        </p>
      )}

      {/* Certains écrans ont besoin d'une deuxième phrase — « voici ce qui
          alimente cette page, voici quoi vérifier » — sans qu'elle devienne
          une deuxième description au même niveau que la première. */}
      {children && (
        <div className="mt-2 max-w-xl text-sm text-muted-foreground">
          {children}
        </div>
      )}

      {(action || (actionLabel && onAction)) && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {action ?? (
            <Button onClick={onAction} size="sm">
              {actionLabel}
            </Button>
          )}
          {secondaryAction}
        </div>
      )}
    </div>
  );
};
EmptyState.displayName = 'EmptyState';

export { EmptyState };
